import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useRack,
  useRackMutations,
  useLocations,
  type Rack,
  type RackStatus,
} from '@/api/capacities';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { NumericInput } from '@/components/numeric-input';

const STATUS_OPTIONS: Array<{ value: RackStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'deprecated', label: 'Deprecated' },
];

interface FormState {
  code: string;
  name: string;
  location_id: string;
  status: RackStatus;
  u_height: string;
  power_supply: string;
  bandwidth_limit: string;
  bandwidth_reality: string;
  description: string;
}

const EMPTY: FormState = {
  code: '',
  name: '',
  location_id: '',
  status: 'active',
  u_height: '42',
  power_supply: '',
  bandwidth_limit: '',
  bandwidth_reality: '',
  description: '',
};

/** Build editable state from an existing rack (numbers → strings for inputs). */
function toForm(r: Rack): FormState {
  return {
    code: r.code ?? '',
    name: r.name ?? '',
    location_id: r.location_id ?? '',
    status: r.status ?? 'active',
    u_height: r.u_height != null ? String(r.u_height) : '42',
    power_supply: r.power_supply != null ? String(r.power_supply) : '',
    bandwidth_limit: r.bandwidth_limit != null ? String(r.bandwidth_limit) : '',
    bandwidth_reality: r.bandwidth_reality != null ? String(r.bandwidth_reality) : '',
    description: r.description ?? '',
  };
}

/** Parse an optional numeric input; blank → undefined, non-numeric → undefined. */
function optNum(v: string): number | undefined {
  const t = v.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** Assemble the `Rack` payload sent to POST/PUT `/racks`. */
function toPayload(f: FormState): Partial<Rack> {
  return {
    code: f.code.trim(),
    name: f.name.trim(),
    location_id: f.location_id,
    status: f.status,
    u_height: optNum(f.u_height) ?? 42,
    power_supply: optNum(f.power_supply),
    bandwidth_limit: optNum(f.bandwidth_limit),
    bandwidth_reality: optNum(f.bandwidth_reality),
    description: f.description.trim() || undefined,
  };
}

/**
 * Rack create/edit form (`/capacities/racks/new` and `/capacities/racks/:id/edit`).
 * When `:id` is present it prefills from `useRack` and PUTs via `update`; otherwise
 * it POSTs a new rack via `create`. Both mutations come from `useRackMutations`.
 * On success it navigates to the rack detail (edit/create) or the list. Location
 * options come from `useLocations`. Mutations are wired for real user execution.
 */
export default function CapacityRackFormPage() {
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id ?? '';
  const isEdit = !!id;

  const { data: rack, isLoading: rackLoading, isError: rackError } = useRack(id);
  const { data: locationsData } = useLocations({ page_size: 500 });
  const { create, update } = useRackMutations();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  // Validate-on-touch: only surface "Required" after a field is blurred or a
  // submit is attempted — not on first render of an empty form.
  const [touched, setTouched] = useState({ code: false, name: false, location: false });
  const [submitted, setSubmitted] = useState(false);

  // Prefill once the rack loads (edit mode only).
  useEffect(() => {
    if (isEdit && rack) setForm(toForm(rack));
  }, [isEdit, rack]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const touch = (key: keyof typeof touched) => setTouched((t) => ({ ...t, [key]: true }));

  const locations = locationsData?.results ?? [];
  const nameInvalid = form.name.trim() === '';
  const codeInvalid = form.code.trim() === '';
  const locationInvalid = form.location_id === '';
  const showCode = (touched.code || submitted) && codeInvalid;
  const showName = (touched.name || submitted) && nameInvalid;
  const showLocation = (touched.location || submitted) && locationInvalid;
  const pending = create.isPending || update.isPending;

  const handleSubmit = () => {
    setSubmitted(true);
    if (codeInvalid || nameInvalid || locationInvalid) {
      setError('Code, name and location are required.');
      return;
    }
    setError(null);
    const payload = toPayload(form);
    const onError = () => setError('Failed to save the rack. Please try again.');

    if (isEdit) {
      update.mutate(
        { id, body: payload },
        { onSuccess: () => navigate(`/capacities/racks/${id}`), onError },
      );
    } else {
      create.mutate(payload, {
        onSuccess: (created) =>
          navigate(created?.id ? `/capacities/racks/${created.id}` : '/capacities/racks'),
        onError,
      });
    }
  };

  const backTo = isEdit ? `/capacities/racks/${id}` : '/capacities/racks';

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 720 }}>
        <div>
          <Button asChild variant="ghost" size="sm">
            <Link
              to={backTo}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}
            >
              <ArrowLeft size={16} />
              {isEdit ? 'Rack' : 'Racks'}
            </Link>
          </Button>
        </div>

        <PageHeader title={isEdit ? 'Edit rack' : 'New rack'} />

        <Card flat>
          <CardBody>
            {isEdit && rackError ? (
              <Alert tone="danger" title="Failed to load rack" />
            ) : isEdit && rackLoading ? (
              <TabLoading rows={6} />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                {error ? <Alert tone="danger" title={error} /> : null}

                <Field label="Code" required error={showCode ? 'Required' : undefined}>
                  {(p) => (
                    <Input
                      {...p}
                      value={form.code}
                      invalid={showCode}
                      onBlur={() => touch('code')}
                      onChange={(e) => set('code', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Name" required error={showName ? 'Required' : undefined}>
                  {(p) => (
                    <Input
                      {...p}
                      value={form.name}
                      invalid={showName}
                      onBlur={() => touch('name')}
                      onChange={(e) => set('name', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Location" required error={showLocation ? 'Required' : undefined}>
                  {(p) => (
                    <Select
                      value={form.location_id}
                      onValueChange={(v) => {
                        set('location_id', v);
                        touch('location');
                      }}
                    >
                      <SelectTrigger id={p.id} invalid={showLocation}>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((l) => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>

                <Field label="Status">
                  {(p) => (
                    <Select
                      value={form.status}
                      onValueChange={(v) => set('status', v as RackStatus)}
                    >
                      <SelectTrigger id={p.id}>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>

                <Field label="U height">
                  {(p) => (
                    <NumericInput
                      {...p}
                      min={1}
                      value={form.u_height}
                      onChange={(e) => set('u_height', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Power supply">
                  {(p) => (
                    <NumericInput
                      {...p}
                      value={form.power_supply}
                      onChange={(e) => set('power_supply', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Bandwidth limit">
                  {(p) => (
                    <NumericInput
                      {...p}
                      value={form.bandwidth_limit}
                      onChange={(e) => set('bandwidth_limit', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Bandwidth reality">
                  {(p) => (
                    <NumericInput
                      {...p}
                      value={form.bandwidth_reality}
                      onChange={(e) => set('bandwidth_reality', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Description">
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={form.description}
                      onChange={(e) => set('description', e.target.value)}
                    />
                  )}
                </Field>

                <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
                  <Button variant="ghost" onClick={() => navigate(backTo)} disabled={pending}>
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} loading={pending}>
                    {isEdit ? 'Save changes' : 'Create rack'}
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </Container>
  );
}
